import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useConversation } from "../lib/useConversation";
import { getScenario } from "@app/shared";

type Props = {
  scenarioId: string;
  onFinished: (result: {
    sessionId: string | null;
    turns: ReturnType<typeof useConversation>["transcript"]["turns"];
    hintsUsed: number;
  }) => void;
};

export function ConversationPage({ scenarioId, onFinished }: Props) {
  const scenario = getScenario(scenarioId);
  const conv = useConversation(scenarioId);
  const [showHint, setShowHint] = useState(false);
  // ?debug=1 のときだけ実機診断ログを画面に出す。音声不達の切り分け用。
  const debugOn =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("debug");

  const hint = useMemo(() => buildHint(conv.turnCount), [conv.turnCount]);

  if (!scenario) return <p>シナリオが見つかりません</p>;

  // 無料体験を使い切った場合。登録での解放は W4 で導線を足す。
  if (conv.quotaExceeded) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-6 text-center">
        <div>
          <h1 className="text-xl font-semibold text-stone-800">
            無料体験の回数を使い切りました
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-stone-600">
            続けて練習するには登録が必要です。まもなく登録できるようになります。
          </p>
        </div>
        <Link
          to="/"
          className="rounded-xl border border-stone-300 bg-white py-3 text-sm font-medium text-stone-700"
        >
          最初に戻る
        </Link>
      </div>
    );
  }

  // AI が話している間は押せない。割り込むと生成がキャンセルされるため。
  const canTalk = conv.state === "ready" || conv.state === "listening";
  const isRecording = conv.state === "listening";

  if (conv.state === "idle") {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-7 p-6">
        <div className="flex flex-col gap-1">
          <span className="text-[13px] text-stone-500">{scenario.situation}</span>
          <h1 className="text-2xl font-semibold leading-[1.4] tracking-[0.01em] text-stone-800">
            {scenario.title}
          </h1>
        </div>

        <p className="text-sm leading-[1.85] text-stone-600">
          {scenario.partnerName}さんと{conv.maxTurns}往復ほど話します。
          <br />
          うまく話せなくて大丈夫です。
          <br />
          詰まったら、ヒントを押してください。
        </p>

        <div className="flex flex-col gap-3">
          <button
            onClick={conv.start}
            className="h-14 rounded-xl bg-stone-800 text-base font-semibold text-white"
          >
            はじめる
          </button>
          <p className="text-center text-xs leading-relaxed text-stone-400">
            はじめると、マイクの使用を確認します
          </p>
        </div>

        {conv.error && <p className="text-sm text-stone-600">{conv.error}</p>}
      </div>
    );
  }

  if (conv.state === "ended") {
    const lastPartnerLine = [...conv.transcript.turns]
      .reverse()
      .find((t) => t.role === "assistant");

    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col p-4">
        <header className="flex items-center justify-between pb-3">
          <span className="text-[13px] text-stone-500">{scenario.title}</span>
          <TurnDots count={conv.turnCount} total={conv.maxTurns} />
        </header>

        <div className="flex flex-1 flex-col justify-center gap-7 px-2">
          {lastPartnerLine && (
            <Turn role="assistant" name={scenario.partnerName}>
              {lastPartnerLine.text}
            </Turn>
          )}

          <div className="flex flex-col gap-2 border-t border-stone-200 pt-6">
            <p className="text-[17px] font-semibold leading-[1.5] text-stone-800">
              {conv.endReason === "disconnected"
                ? "接続が切れました"
                : "会話が終わりました"}
            </p>
            <p className="text-sm leading-[1.7] text-stone-600">
              {scenario.partnerName}さんと{conv.turnCount}往復、話しました。
            </p>
          </div>
        </div>

        <button
          onClick={() =>
            onFinished({
              sessionId: conv.getSessionId(),
              turns: conv.transcript.turns,
              hintsUsed: conv.hintsUsed,
            })
          }
          className="mt-3 h-14 rounded-xl bg-stone-800 text-base font-semibold text-white"
        >
          ふり返りを見る
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col p-4">
      {debugOn && <DebugOverlay lines={conv.debugLog} />}
      <header className="flex items-center justify-between pb-3">
        <span className="text-[13px] text-stone-500">{scenario.title}</span>
        <TurnDots count={conv.turnCount} total={conv.maxTurns} />
      </header>

      <div className="flex flex-1 flex-col gap-5 overflow-y-auto pb-4">
        {/*
          文字起こしは「概ね正確」止まり。誤認識を「あなたはこう言いました」と
          見せないため、画面上でも聞き取り結果であることを最初に断っておく。
        */}
        <p className="text-[11px] leading-normal text-stone-400">
          画面の文字は音声の聞き取り結果です
        </p>

        {conv.transcript.turns.map((turn, i) => (
          <Turn key={i} role={turn.role} name={scenario.partnerName}>
            {turn.text}
          </Turn>
        ))}
        {conv.transcript.partialUser && (
          <Turn role="user" name={scenario.partnerName} partial>
            {conv.transcript.partialUser}
          </Turn>
        )}
        {conv.transcript.partialAssistant && (
          <Turn role="assistant" name={scenario.partnerName} partial speaking>
            {conv.transcript.partialAssistant}
          </Turn>
        )}
      </div>

      {showHint && (
        <div className="mb-3 flex flex-col gap-1.5 rounded-xl bg-[#f6f1e7] px-4 py-3.5">
          <p className="text-[13px] leading-[1.6] text-stone-500">{hint.ja}</p>
          <p className="text-base font-semibold leading-[1.5] text-stone-800">
            {hint.en}
          </p>
        </div>
      )}

      {conv.needsResume && (
        <button
          onClick={conv.resume}
          className="mb-3 flex h-[52px] items-center justify-center gap-2 rounded-xl border border-stone-300 bg-white text-sm font-semibold text-stone-700"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 4l14 8-14 8z" />
          </svg>
          一時停止中 — タップして続きから
        </button>
      )}

      {/* エラーは赤で出さない。この画面でいちばん怖がらせたくない層が読む。 */}
      {conv.error && (
        <p className="mb-2 text-center text-sm text-stone-600">{conv.error}</p>
      )}

      <div className="flex items-stretch gap-2 pb-2">
        <button
          onClick={() => {
            if (!showHint) conv.useHint();
            setShowHint((v) => !v);
          }}
          disabled={isRecording}
          className={[
            "shrink-0 rounded-xl border px-4 text-sm transition-colors",
            isRecording
              ? "border-stone-200 text-stone-300"
              : showHint
                ? "border-stone-900 bg-stone-900 text-white"
                : "border-stone-300 text-stone-600",
          ].join(" ")}
        >
          ヒント
        </button>

        <button
          onPointerDown={(e) => {
            e.preventDefault();
            // ポインタを捕捉する。捕捉しないと、押したまま指やカーソルがボタンの
            // 外にずれた瞬間に pointerleave が飛び、話の途中で録音が切れる。
            e.currentTarget.setPointerCapture(e.pointerId);
            conv.pressToTalk();
          }}
          onPointerUp={(e) => {
            e.preventDefault();
            conv.releaseToTalk();
            setShowHint(false);
          }}
          onPointerCancel={conv.releaseToTalk}
          // 捕捉に失敗した環境向けの保険。捕捉できている間はここで解放しない。
          onPointerLeave={(e) => {
            if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
              conv.releaseToTalk();
            }
          }}
          // 捕捉をOSやブラウザに取り上げられたら、押しっぱなしのまま放置しない。
          onLostPointerCapture={conv.releaseToTalk}
          onContextMenu={(e) => e.preventDefault()}
          disabled={!canTalk}
          className={[
            "flex h-[72px] flex-1 select-none touch-none items-center justify-center gap-2.5 rounded-xl text-base font-semibold transition-colors",
            isRecording
              ? "bg-stone-900 text-white shadow-[0_0_0_6px_rgba(41,37,36,0.09)]"
              : canTalk
                ? "bg-stone-800 text-white"
                : "bg-stone-200 text-stone-400",
          ].join(" ")}
        >
          <TalkButtonFace state={conv.state} name={scenario.partnerName} />
        </button>
      </div>
    </div>
  );
}

/**
 * 実機診断用のログパネル。?debug=1 のときだけ表示する。
 * Android/iOS 実機で音声不達を切り分けるため、verify2 と同じく画面にログを出す。
 */
function DebugOverlay({ lines }: { lines: string[] }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 max-h-[38vh] overflow-y-auto whitespace-pre-wrap break-all bg-black/85 p-2 font-mono text-[10px] leading-snug text-green-300">
      {lines.length === 0 ? "（ログ待機中…）" : lines.join("\n")}
    </div>
  );
}

/**
 * 往復数は「3 / 8」ではなくドットで出す。
 * 数字は達成度の採点に見え、この層に「測られている」感覚を与えるため。
 */
function TurnDots({ count, total }: { count: number; total: number }) {
  return (
    <div
      className="flex items-center gap-[5px]"
      aria-label={`${total}往復のうち${count}往復`}
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={[
            "size-[5px] rounded-full",
            i < count ? "bg-stone-500" : "bg-stone-300",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

/**
 * 吹き出しは使わない。色の面で囲うと採点表の見た目に近づくため、
 * 話者ラベルとインデント、文字の濃さだけで自分と相手を分ける。
 */
function Turn({
  role,
  name,
  partial,
  speaking,
  children,
}: {
  role: "user" | "assistant";
  name: string;
  partial?: boolean;
  speaking?: boolean;
  children: React.ReactNode;
}) {
  const isUser = role === "user";
  return (
    <div
      className={[
        "flex flex-col gap-[3px]",
        isUser ? "pl-7" : "",
        partial ? "opacity-55" : "",
      ].join(" ")}
    >
      <span className="flex items-center gap-1.5 text-[11px] tracking-[0.02em] text-stone-400">
        {isUser ? "あなた" : name}
        {speaking && <LevelBars className="h-[9px]" barClassName="bg-stone-400" heights={[5, 9, 6]} width={2} />}
      </span>
      <p
        className={[
          "text-[15px] leading-[1.65]",
          isUser ? "text-stone-600" : "text-stone-800",
        ].join(" ")}
      >
        {children}
      </p>
    </div>
  );
}

/**
 * 録音中を赤で示さない。赤は「記録されている／見張られている」印象になり、
 * いちばん怖がらせたくない瞬間に怖さを足すため。濃さとリングとバーで示す。
 */
function TalkButtonFace({ state, name }: { state: string; name: string }) {
  switch (state) {
    case "connecting":
      return <span>接続中…</span>;
    case "listening":
      return (
        <>
          <LevelBars
            className="h-5"
            barClassName="bg-white"
            heights={[8, 16, 11, 19, 7]}
            width={3}
          />
          <span>聞いています</span>
        </>
      );
    case "thinking":
      return <span>…</span>;
    case "speaking":
      return <span>{name}が話しています</span>;
    default:
      return (
        <>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M5 11a7 7 0 0 0 14 0" />
            <path d="M12 18v3" />
          </svg>
          <span>押しながら話す</span>
        </>
      );
  }
}

/**
 * 音量の実測値ではなく「生きている」ことだけを示す装飾。
 * 入力レベルに連動させるなら ConversationEngine 側の露出が要る。
 */
function LevelBars({
  className,
  barClassName,
  heights,
  width,
}: {
  className: string;
  barClassName: string;
  heights: number[];
  width: number;
}) {
  return (
    <span className={`flex items-end gap-[2px] ${className}`} aria-hidden="true">
      {heights.map((h, i) => (
        <span
          key={i}
          className={`level-bar rounded-full ${barClassName}`}
          style={{
            width: `${width}px`,
            height: `${h}px`,
            animationDelay: `${i * 90}ms`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * MVP では固定文言。ターゲット下限の層は英語力以前に
 * 「何を言えばいいか」が日本語でも固まっていないため、
 * 日本語の方向づけを先に出し、英語例を添える。
 */
function buildHint(turnCount: number): { ja: string; en: string } {
  if (turnCount === 0) {
    return {
      ja: "まずは一言だけ返して、同じ質問を返してみましょう。",
      en: "It was good. How about you?",
    };
  }
  if (turnCount < 3) {
    return {
      ja: "「良かった」で終わらせず、何をしたか一つ足してみましょう。",
      en: "I went to a cafe with my family.",
    };
  }
  return {
    ja: "相手の話に「それいいね」と反応してから、質問を返してみましょう。",
    en: "Oh, that sounds nice. Where did you go?",
  };
}
